// src/components/ReferralShareOptions.jsx
import React from 'react';
import { Modal, Button, Row, Col, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faWhatsapp, faFacebookF, faXTwitter, faRedditAlien,
    faTelegramPlane, faTiktok, faInstagram, faFacebookMessenger
} from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faClipboard, faShareNodes } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import './ReferralShareOptions.css';

const ReferralShareOptions = ({ referralLink, onClose }) => {
    const { t } = useTranslation();

    const rawShareMessage = t('referral_share_message', { link: referralLink });
    const shareMessage = encodeURIComponent(rawShareMessage);
    const shareTitle = encodeURIComponent(t('referral_share_title', { defaultValue: 'Opportunité d\'Investissement AI' }));

    const socialPlatforms = [
        { name: 'WhatsApp', icon: faWhatsapp, color: '#25D366', url: `https://api.whatsapp.com/send?text=${shareMessage}` },
        { name: 'Facebook', icon: faFacebookF, color: '#1877F2', url: `https://www.facebook.com/sharer/sharer.php?u=${referralLink}&quote=${shareMessage}` },
        { name: 'X (Twitter)', icon: faXTwitter, color: '#000000', url: `https://twitter.com/intent/tweet?text=${shareTitle}&url=${referralLink}` },
        { name: 'Telegram', icon: faTelegramPlane, color: '#0088CC', url: `https://t.me/share/url?url=${referralLink}&text=${shareMessage}` },
        { name: 'Gmail', icon: faEnvelope, color: '#D44638', url: `mailto:?subject=${shareTitle}&body=${shareMessage}` },
        { name: 'Reddit', icon: faRedditAlien, color: '#FF4500', url: `https://www.reddit.com/submit?url=${referralLink}&title=${shareTitle}` },
        { name: 'Messenger', icon: faFacebookMessenger, color: '#00B2FF', url: `https://www.facebook.com/dialog/send?link=${referralLink}&app_id=YOUR_APP_ID_HERE&redirect_uri=${window.location.href}` },
        { name: 'Instagram', icon: faInstagram, color: '#E1306C', url: '#', copy: true },
        { name: 'TikTok', icon: faTiktok, color: '#000000', url: '#', copy: true },
    ];

    const handleCopyLink = () => {
        navigator.clipboard.writeText(referralLink)
            .then(() => {
                toast.success(t("toast_copy_success"));
            })
            .catch(err => {
                console.error('Erreur lors de la copie du lien:', err);
                toast.error(t("toast_copy_error"));
            });
    };

    const handlePlatformClick = (platform) => {
        if (platform.copy) {
            handleCopyLink();
        }
    };

    return (
        <Modal 
            show={true} 
            onHide={onClose} 
            centered 
            className="referral-share-modal-wide"
            dialogClassName="wide-modal-dialog"
        >
            <Modal.Header closeButton className="referral-modal-header-wide">
                <Modal.Title className="text-white">
                    <FontAwesomeIcon icon={faShareNodes} className="me-2" />
                    {t('share_referral_link')}
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body className="p-3 wide-modal-body">
                {/* Section compacte lien + réseaux sociaux côte à côte */}
                <Row className="g-3 align-items-stretch">
                    {/* Colonne Lien de parrainage */}
                    <Col md={5}>
                        <Card className="h-100 referral-link-card-wide">
                            <Card.Body className="d-flex flex-column justify-content-center p-4">
                                <h6 className="fw-bold text-primary mb-3">
                                    {t('your_unique_referral_link_instruction')}
                                </h6>
                                
                                <div className="referral-link-container-wide">
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm referral-link-input-wide"
                                            value={referralLink}
                                            readOnly
                                        />
                                        <Button
                                            variant="primary"
                                            onClick={handleCopyLink}
                                            className="btn-copy-link-wide"
                                            size="sm"
                                        >
                                            <FontAwesomeIcon icon={faClipboard} />
                                        </Button>
                                    </div>
                                </div>
                                
                                <p className="text-muted small mt-2 mb-0">
                                    {t('referral_copy_paste_instruction')}
                                </p>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Colonne Réseaux sociaux - SEULEMENT LES ICÔNES */}
                    <Col md={7}>
                        <div className="social-section-wide h-100">
                            <h6 className="section-title-wide mb-3">
                                <FontAwesomeIcon icon={faShareNodes} className="me-2" />
                                {t('share_on_social_media')}
                            </h6>
                            
                            <Row className="g-2 social-buttons-grid-wide">
                                {socialPlatforms.map((platform) => (
                                    <Col xs={4} key={platform.name}>
                                        {platform.copy ? (
                                            <Button
                                                variant="light"
                                                className="social-button-wide rectangular-button-wide w-100"
                                                onClick={() => handlePlatformClick(platform)}
                                                title={platform.name} // Tooltip avec le nom
                                            >
                                                <FontAwesomeIcon 
                                                    icon={platform.icon} 
                                                    className="social-icon-wide-large"
                                                    style={{ color: platform.color }}
                                                />
                                                {/* SUPPRIMÉ : <span className="platform-name-wide">{platform.name}</span> */}
                                            </Button>
                                        ) : (
                                            <a
                                                href={platform.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-light social-button-wide rectangular-button-wide w-100"
                                                title={platform.name} // Tooltip avec le nom
                                            >
                                                <FontAwesomeIcon 
                                                    icon={platform.icon} 
                                                    className="social-icon-wide-large"
                                                    style={{ color: platform.color }}
                                                />
                                                {/* SUPPRIMÉ : <span className="platform-name-wide">{platform.name}</span> */}
                                            </a>
                                        )}
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </Col>
                </Row>
            </Modal.Body>
            
            <Modal.Footer className="referral-modal-footer-wide py-2">
                <Button variant="outline-secondary" onClick={onClose} size="sm">
                    {t('button_close')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ReferralShareOptions;